import { QueryClientProvider } from "@tanstack/react-query";
import { useStore } from "@tanstack/react-store";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import DidomiConsent from "./components/DidomiConsent";
import { BackgroundPreloaderProvider } from "./contexts/BackgroundPreloaderContext";
import {
    ModaleLocationProvider,
    useModaleLocation,
} from "./contexts/ModaleLocationContext";
import useSyncLanguageSettings from "./hooks/settings/useSyncLanguageSettings";
import useFeatureFlag from "./hooks/useFeatureFlag";
import useGetUserEmailFromAuth from "./hooks/useGetUserEmailFromAuth";
import useServiceErrorListener from "./hooks/useServiceErrorListener";
import { FloatingLogSettings } from "./lib/logging/FloatingLogSettings";
import queryClient from "./lib/queryClient";
import NotFound from "./presentation/404/NotFound";
import AskConsent from "./presentation/AskConsent";
import LoggingOutScreen from "./presentation/auth/LoggingOutScreen";
import CheckConsent from "./presentation/CheckConsent";
import CheckLicense from "./presentation/CheckLicense";
import CheckServiceInitialization from "./presentation/CheckServiceInitialization";
import DashboardLayout from "./presentation/dashboard/DashboardLayout";
import CustomViewEmails from "./presentation/dashboard/modalPages/mails/CustomViewEmails";
import GeneralEmails from "./presentation/dashboard/modalPages/mails/GeneralEmails";
import SmartLabelEmails from "./presentation/dashboard/modalPages/mails/SmartLabelEmails";
import NavbarLayout from "./presentation/dashboard/modalPages/NavbarLayout";
import B2CNotAllowedScreen from "./presentation/license/B2CNotAllowedScreen";
import CancelPage from "./presentation/license/CancelPage";
import EnterpriseForm from "./presentation/license/EnterpriseForm";
import SelectLicense from "./presentation/license/SelectLicense";
import SuccessPage from "./presentation/license/SuccessPage";
import ServiceErrorPage from "./presentation/serviceError/ServiceErrorPage";
import CheckAuthentication from "./router/CheckAuthentication";
import routes from "./router/routes";
import authStore from "./stores/authStore";

const isNonProductionEnvironment =
    import.meta.env.VITE_APP_ENVIRONMENT === "local" ||
    import.meta.env.VITE_APP_ENVIRONMENT === "development" ||
    import.meta.env.VITE_APP_ENVIRONMENT === "recette";

const AppRoutes = () => {
    const { modaleLocation } = useModaleLocation();
    const { isSystemLogsEnabled } = useFeatureFlag();

    useSyncLanguageSettings();
    useGetUserEmailFromAuth();

    const showFloatingLogSettings =
        isSystemLogsEnabled || isNonProductionEnvironment;

    return (
        <>
            {showFloatingLogSettings && <FloatingLogSettings />}
            <DidomiConsent />
            {/* SDK didomi instance, has to be available through the app for constent management */}

            <Routes location={modaleLocation}>
                <Route element={<CheckAuthentication />}>
                    <Route element={<CheckConsent />}>
                        <Route element={<CheckServiceInitialization />}>
                            <Route
                                element={
                                    <Navigate to={routes.dashboard} replace />
                                }
                                path={routes.root}
                            />
                            <Route element={<CheckLicense />}>
                                <Route element={<DashboardLayout />}>
                                    <Route
                                        element={<div />}
                                        path={routes.dashboard}
                                    />
                                    <Route element={<NavbarLayout />}>
                                        <Route
                                            element={<GeneralEmails />}
                                            path={routes.inbox}
                                        />
                                        <Route
                                            element={<GeneralEmails />}
                                            path={routes.sent}
                                        />
                                        <Route
                                            element={<GeneralEmails />}
                                            path={routes.treated}
                                        />
                                        <Route
                                            element={<GeneralEmails />}
                                            path={routes.draft}
                                        />
                                        <Route
                                            element={<GeneralEmails />}
                                            path={routes.trash}
                                        />
                                        <Route
                                            element={<GeneralEmails />}
                                            path={routes.spam}
                                        />
                                        <Route
                                            element={<SmartLabelEmails />}
                                            path={routes.smartLabel}
                                        />
                                        <Route
                                            element={<CustomViewEmails />}
                                            path={routes.customViews}
                                        />
                                    </Route>
                                </Route>
                            </Route>
                            <Route
                                element={<SelectLicense />}
                                path={routes.selectLicense}
                            />
                            <Route
                                element={<EnterpriseForm />}
                                path={routes.enterpriseForm}
                            />
                        </Route>
                    </Route>
                    <Route element={<AskConsent />} path={routes.askConsent} />
                </Route>
                <Route element={<SuccessPage />} path={routes.success} />
                <Route element={<CancelPage />} path={routes.cancel} />
                <Route element={<NotFound />} path={routes.notFound} />
            </Routes>
        </>
    );
};

const AppContent = () => {
    const isLoggingOut = useStore(authStore, (state) => state.isLoggingOut);
    const { hasError, isB2CNotAllowed } = useServiceErrorListener();

    if (isLoggingOut) {
        return <LoggingOutScreen />;
    }

    if (isB2CNotAllowed) {
        return <B2CNotAllowedScreen />;
    }

    if (hasError) {
        return <ServiceErrorPage />;
    }

    return <AppRoutes />;
};

const App = () => {
    return (
        <QueryClientProvider client={queryClient}>
            <BrowserRouter>
                <BackgroundPreloaderProvider>
                    <ModaleLocationProvider>
                        <AppContent />
                    </ModaleLocationProvider>
                </BackgroundPreloaderProvider>
            </BrowserRouter>
        </QueryClientProvider>
    );
};

export default App;
